import React from 'react';
import './BetaTesting.css';
import EarlyAccessForm from '../../components/EarlyAccessForm/EarlyAccessForm';
import BetaStats from '../../components/BetaStats/BetaStats';
import ZoomableImage from '../../components/ZoomableImage/ZoomableImage';

const dataDictionary = [
  {
    title: 'Fitter & Dancer Context',
    groups: [
      { label: 'Fitter', items: ['Experience'] },
      { label: 'Dancer', items: ['Experience', 'Age started pointe', 'Weight (approximate)', 'Any notes (hereditary bunions, injury, etc.)'] },
    ],
  },
  {
    title: 'Anatomical Information',
    items: [
      'Foot length',
      'Forefoot width',
      'Metatarsal circumference',
      'Foot compressibility',
      'Toe 1, 2, and 3 length',
      'Hallux angle',
      'Arch height',
      'Arch length',
      'Ankle ROM',
      'Heel width',
      'Heel height',
    ],
  },
  {
    title: 'Positions Recorded',
    items: [
      'Second position demi plié, flat',
      'Parallel, on pointe',
      'Fifth position, on pointe',
      'Timed hold, on pointe (8 seconds), to see how the foot sinks over time',
      'Bourrée, on pointe (5 seconds)',
    ],
  },
  {
    title: 'Fit Labels',
    groups: [
      { label: 'Overall', items: ['Good / okay / bad fit', 'Right shape, wrong size', 'Mild / moderate / severe pain'] },
      {
        label: 'Box',
        items: [
          'Box too narrow/wide',
          'Box too tapered/straight',
          'Box too much volume',
          'Upper box too wide/tight',
          'Lower box too wide/narrow',
          'Crown too high/low',
          'Wings too low/high',
        ],
      },
      { label: 'Shank', items: ['Shank too hard/soft', 'Breaking too low/high'] },
      { label: 'Size', items: ['Shoe too long/short', 'Vamp too long/short', 'Platform too narrow/wide'] },
    ],
  },
  {
    title: 'Ambiguous Labels',
    items: ['Sinking', 'Crushing', 'Floating', 'Over box', 'Pulled back', 'Twisted'],
  },
];

const BetaTesting = () => {
  return (
    <div className="beta-container">
      {/* Hero */}
      <div
        className="beta-hero-section"
        style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/IMG_9401.jpg)` }}
      >
        <div className="beta-hero-overlay">
          <h1 className="beta-hero-title">Call for Beta Testers</h1>
          <p className="beta-hero-subtitle">Pointe Shoe Fit Study</p>
        </div>
      </div>

      {/* Intro */}
      <section className="beta-section">
        <div className="beta-content-wrapper">
          <p className="beta-text">
            We&rsquo;re calling for participants for our beta testing study on pointe shoe fit.
            We&rsquo;re looking for dancers and fitters who can work together to collect data on pointe shoe fit issues.
          </p>
          <BetaStats />
          <p className="beta-host-cta">
            Want to host testing? <a href="#get-involved">Sign up here.</a>
          </p>
        </div>
      </section>

      {/* Looking for */}
      <section className="beta-section beta-section-tint">
        <div className="beta-content-wrapper">
          <h2 className="beta-heading">What We&rsquo;re Looking For</h2>

          <div className="beta-group-grid">
            <div className="beta-group-card">
              <h3 className="beta-group-title">Dance Stores & Fitters</h3>
              <p className="beta-group-intro">
                Dance stores with experienced pointe shoe fitters willing to host 2&ndash;3 hour test
                sessions.
              </p>
              <ul className="beta-list">
                <li>Fitters should be comfortable using a mobile app (on an iPad or phone) and able to identify the fit features we&rsquo;re looking to label</li>
                <li>Flagship stores are very much part of this</li>
                <li>Stores and fitters need to be willing to attend a training seminar to learn how our tool works</li>
                <li>We&rsquo;re open to all types of collaborations, from paid pilots where your store gets early access to the technology, to loaned technology to help improve our data collection</li>
              </ul>
            </div>

            <div className="beta-group-card">
              <h3 className="beta-group-title">Dancers</h3>
              <p className="beta-group-intro">
                Dancers from varied experience backgrounds willing to try on 8&ndash;10 pairs of
                pointe shoes and share their data for training a tool to help dancers in the future.
              </p>
              <ul className="beta-list">
                <li>Data includes anatomical foot measurements, approximate weight, experience, and raw sensor data collected</li>
                <li>All participants will sign appropriate consent and data privacy forms before their data is collected and/or trained on</li>
                <li>We&rsquo;re very open to dancers early in their pointe journey. We&rsquo;re trying to identify the problems that show up especially early in pointe education</li>
              </ul>
            </div>
          </div>

          <p className="beta-text beta-collab-note">
            We&rsquo;re also open to collaborations with brands and research institutions for
            individualized, curated use of our sensor to meet their research or brand goals. For any
            inquiries into technology capabilities, please contact Kaitlyn at{' '}
            <a href="mailto:kaitlyn@pointesense.com">kaitlyn@pointesense.com</a>.
          </p>
        </div>
      </section>

      {/* What the study collects */}
      <section className="beta-section">
        <div className="beta-content-wrapper">
          <h2 className="beta-heading">What This Study Is Collecting</h2>
          <p className="beta-text">
            We&rsquo;ll first collect anatomic data on the dancer&rsquo;s feet, such as size, shape,
            compressibility, and arch type.
          </p>
          <p className="beta-text">
            Dancers will try on 8&ndash;10 pairs of pointe shoes and stand in typical fitting
            positions: second position demi pli&eacute; on flat, parallel on pointe, and fifth position on pointe. They&rsquo;ll also do two short timed trials on pointe: an 8-second hold to capture how the foot sinks over time, and a 5-second bourr&eacute;e.
          </p>
          <p className="beta-text">
            Fitters will need to feel comfortable using our app to label observed fit problems or
            identify good fit, and will need to be thorough about checking for every possible
            problem. They&rsquo;ll also intentionally select a few pairs they expect to be poor fits
            for the dancer, to collect enough &ldquo;bad&rdquo; data.
          </p>
          <p className="beta-statement">We&rsquo;ll be starting this in Fall 2026.</p>
        </div>
      </section>

      {/* Sample data card */}
      <section className="beta-section beta-section-tint">
        <div className="beta-content-wrapper">
          <h2 className="beta-heading">A Look at the Data</h2>
          <p className="beta-text">
            This is a small fraction of what each test produces: the same dancer in two different
            shoes, with the fitter&rsquo;s labels alongside how pressure was shared across the foot,
            how the foot sank over a timed hold, and where hotspots formed. We&rsquo;re hoping this
            data will show what goes into a good pointe shoe fit, and how pointe shoes actually
            support dancers&rsquo; feet. Tap the card to enlarge it.
          </p>
          <ZoomableImage
            src={`${process.env.PUBLIC_URL}/images/PS/beta_card_wide_1600.png`}
            alt="Beta test data card comparing two shoes on the same dancer, with fit labels, pressure heatmaps, sinking maps, load sharing and hotspots"
            className="beta-data-card"
            wide
          />
        </div>
      </section>

      {/* Get involved / form */}
      <section className="beta-section beta-cta-section" id="get-involved">
        <div className="beta-content-wrapper beta-cta-content">
          <h2 className="beta-heading light">Get Involved</h2>
          <p className="beta-hero-subtext">
            If you&rsquo;re interested in being part of this collaboration, fill out the form below.
            In the optional field, include the phrase &ldquo;BETA TESTING&rdquo; along with what month you would be interested in the collab and how many
            fitters and/or dancers you might be able to provide or host.
          </p>
          <div className="beta-form-card">
            <EarlyAccessForm idPrefix="beta" submitLabel="Submit Interest" />
          </div>
        </div>
      </section>

      {/* Data dictionary */}
      <section className="beta-section beta-section-tint">
        <div className="beta-content-wrapper beta-dictionary-wrapper">
          <h2 className="beta-heading">More Detail on What Data Is Being Collected</h2>
          <div className="beta-dictionary-grid">
            {dataDictionary.map((card) => (
              <div className="beta-dictionary-card" key={card.title}>
                <h4>{card.title}</h4>
                {card.groups ? (
                  card.groups.map((sub) => (
                    <div className="beta-dictionary-subgroup" key={sub.label}>
                      <h5>{sub.label}</h5>
                      <ul>
                        {sub.items.map((item) => (
                          <li key={item}>{item}</li>
                        ))}
                      </ul>
                    </div>
                  ))
                ) : (
                  <ul>
                    {card.items.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default BetaTesting;
